<template>
  <div v-if="field" class="field-editor-panel">
    <div class="panel-header">
      <h4>{{ t('registration.editField') }}</h4>
      <span class="field-type-badge">{{ typeLabel }}</span>
    </div>

    <div class="editor-content">
      <div class="form-group">
        <label>{{ t('registration.fieldType') }} *</label>
        <select
          v-model="field.type"
          @change="$emit('typeChange')"
          class="field-type-select"
        >
          <option value="text">{{ t('registration.fieldTypes.text') }}</option>
          <option value="email">
            {{ t('registration.fieldTypes.email') }}
          </option>
          <option value="name">{{ t('registration.fieldTypes.name') }}</option>
          <option value="number">
            {{ t('registration.fieldTypes.number') }}
          </option>
          <option value="textarea">
            {{ t('registration.fieldTypes.textarea') }}
          </option>
          <option value="select">
            {{ t('registration.fieldTypes.select') }}
          </option>
          <option value="radio">
            {{ t('registration.fieldTypes.radio') }}
          </option>
          <option value="checkbox">
            {{ t('registration.fieldTypes.checkbox') }}
          </option>
          <option value="date">{{ t('registration.fieldTypes.date') }}</option>
          <option value="time">{{ t('registration.fieldTypes.time') }}</option>
          <option value="section">
            {{ t('registration.fieldTypes.section') }}
          </option>
        </select>
      </div>

      <!-- Dropdown: label for the empty first option -->
      <div v-if="field.type === 'select'" class="form-group">
        <label>{{ t('registration.selectEmptyOptionLabel') }}</label>
        <input
          v-model.lazy="field.selectEmptyOption"
          type="text"
          :placeholder="t('registration.selectOption')"
        />
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>{{ t('registration.fieldLabel') }} *</label>
          <input
            v-model.lazy="field.label"
            type="text"
            required
            :placeholder="t('registration.fieldLabelPlaceholder')"
          />
        </div>

        <div v-if="field.type !== 'section'" class="form-group">
          <label>{{ t('registration.fieldName') }} *</label>
          <div class="field-name-container">
            <input
              v-model.lazy="field.name"
              type="text"
              required
              :placeholder="t('registration.fieldNamePlaceholder')"
              :disabled="!manuallyEdited"
              @input="$emit('sanitizeName')"
              :class="{
                error: duplicateNames.includes(field.name),
                'auto-generated': !manuallyEdited,
              }"
            />
            <button
              v-if="!manuallyEdited"
              @click.prevent="$emit('enableManual')"
              class="btn btn-secondary btn-sm edit-name-btn"
              type="button"
            >
              Edit
            </button>
            <button
              v-else
              @click.prevent="$emit('disableManual')"
              class="btn btn-outline btn-sm edit-name-btn"
              type="button"
            >
              Auto
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="field.type !== 'section' && field.type !== 'select'"
        class="form-group"
      >
        <label>{{ t('registration.fieldPlaceholder') }}</label>
        <input
          v-model.lazy="field.placeholder"
          type="text"
          :placeholder="t('registration.fieldPlaceholderPlaceholder')"
        />
      </div>

      <!-- Name field translations (per field) -->
      <div v-if="field.type === 'name'" class="form-row">
        <div class="form-group">
          <label>{{
            t('registration.public.firstName') || 'First Name'
          }}</label>
          <input
            v-model.lazy="field.firstNameLabel"
            type="text"
            :placeholder="t('registration.public.firstName') || 'First Name'"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.public.lastName') || 'Last Name' }}</label>
          <input
            v-model.lazy="field.lastNameLabel"
            type="text"
            :placeholder="t('registration.public.lastName') || 'Last Name'"
          />
        </div>
      </div>

      <!-- Date field translations (per field) -->
      <div v-if="field.type === 'date'" class="form-row">
        <div class="form-group">
          <label>{{ t('registration.dayLabelText') }}</label>
          <input
            v-model.lazy="field.dayLabel"
            type="text"
            :placeholder="t('registration.dayLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.dayPlaceholderText') }}</label>
          <input
            v-model.lazy="field.dayPlaceholder"
            type="text"
            :placeholder="t('registration.dayPlaceholderPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.monthLabelText') }}</label>
          <input
            v-model.lazy="field.monthLabel"
            type="text"
            :placeholder="t('registration.monthLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.monthPlaceholderText') }}</label>
          <input
            v-model.lazy="field.monthPlaceholder"
            type="text"
            :placeholder="t('registration.monthPlaceholderPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.yearLabelText') }}</label>
          <input
            v-model.lazy="field.yearLabel"
            type="text"
            :placeholder="t('registration.yearLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.yearPlaceholderText') }}</label>
          <input
            v-model.lazy="field.yearPlaceholder"
            type="text"
            :placeholder="t('registration.yearPlaceholderPlaceholder')"
          />
        </div>
      </div>

      <div v-if="field.type === 'date'" class="form-group">
        <label>{{ t('registration.monthNames') }}</label>
        <div class="month-names-grid">
          <div
            v-for="n in 12"
            :key="'month-name-' + (n - 1)"
            class="month-name-item"
          >
            <span class="month-name-caption">{{
              defaultMonthNames[n - 1]
            }}</span>
            <input
              :value="monthNamesSafe[n - 1]"
              @input="setMonthName(n - 1, $event.target.value)"
              type="text"
              :placeholder="defaultMonthNames[n - 1]"
            />
          </div>
        </div>
      </div>

      <div v-if="field.type !== 'section'" class="form-group inline">
        <label
          ><input v-model="field.required" type="checkbox" />
          {{ t('registration.fieldRequired') }}</label
        >
      </div>

      <div
        v-if="['select', 'radio', 'checkbox'].includes(field.type)"
        class="form-group"
      >
        <label>{{ t('registration.fieldOptions') }}</label>
        <div class="options-list">
          <div
            v-for="(option, optionIndex) in field.options"
            :key="optionIndex"
            class="option-item"
          >
            <input
              v-model.lazy="field.options[optionIndex]"
              type="text"
              :placeholder="`Option ${optionIndex + 1}`"
            />
            <div class="field-actions option-actions">
              <button
                type="button"
                @click.prevent="$emit('moveOption', optionIndex, -1)"
                :disabled="optionIndex === 0"
                class="btn-icon"
                :title="t('general.moveUp') || 'Move Up'"
              >
                <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 3l-4 4h3v6h2V7h3L8 3z" />
                </svg>
              </button>
              <button
                type="button"
                @click.prevent="$emit('moveOption', optionIndex, 1)"
                :disabled="optionIndex === field.options.length - 1"
                class="btn-icon"
                :title="t('general.moveDown') || 'Move Down'"
              >
                <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 13l4-4H9V3H7v6H4l4 4z" />
                </svg>
              </button>
              <button
                type="button"
                @click.prevent="$emit('removeOption', optionIndex)"
                class="btn-icon btn-danger"
                :title="t('general.delete') || 'Delete'"
              >
                <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M4.2 3l3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            type="button"
            @click.prevent="$emit('addOption')"
            class="btn btn-secondary btn-sm"
          >
            {{ t('registration.addOption') }}
          </button>
        </div>
      </div>

      <div v-if="hasAnyValidationControls" class="form-group">
        <label>{{ t('registration.validation') }}</label>
        <div class="validation-grid">
          <div
            v-if="['text', 'textarea'].includes(field.type)"
            class="validation-item"
          >
            <label>{{ t('registration.minLength') }}</label>
            <input
              :value="field.validation && field.validation.minLength"
              @input="updateValidation('minLength', $event.target.value)"
              type="number"
              placeholder="Min"
            />
          </div>
          <div
            v-if="['text', 'textarea'].includes(field.type)"
            class="validation-item"
          >
            <label>{{ t('registration.maxLength') }}</label>
            <input
              :value="field.validation && field.validation.maxLength"
              @input="updateValidation('maxLength', $event.target.value)"
              type="number"
              placeholder="Max"
            />
          </div>
          <div v-if="field.type === 'number'" class="validation-item">
            <label>{{ t('registration.minValue') }}</label>
            <input
              :value="field.validation && field.validation.min"
              @input="updateValidation('min', $event.target.value)"
              type="number"
              placeholder="Min"
            />
          </div>
          <div v-if="field.type === 'number'" class="validation-item">
            <label>{{ t('registration.maxValue') }}</label>
            <input
              :value="field.validation && field.validation.max"
              @input="updateValidation('max', $event.target.value)"
              type="number"
              placeholder="Max"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'FieldEditor',
  props: {
    field: { type: Object, required: false },
    manuallyEdited: { type: Boolean, required: true },
    duplicateNames: { type: Array, required: true },
  },
  emits: [
    'typeChange',
    'sanitizeName',
    'enableManual',
    'disableManual',
    'addOption',
    'moveOption',
    'removeOption',
  ],
  setup(props, { emit }) {
    const { t, locale } = useI18n()

    const EN_CALENDAR_FALLBACK = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    const defaultMonthNames = computed(() => {
      locale.value
      return Array.from({ length: 12 }, (_, i) => {
        const key = `registration.calendarMonths.${i + 1}`
        const name = t(key)
        if (name === key) return EN_CALENDAR_FALLBACK[i]
        return name
      })
    })

    const ensureDateFieldDefaults = (field) => {
      if (!field || field.type !== 'date') return
      if (!Array.isArray(field.monthNames) || field.monthNames.length !== 12) {
        field.monthNames = [...defaultMonthNames.value]
      }
      const setIfEmpty = (prop, msgKey) => {
        const v = field[prop]
        if (v === undefined || v === null || String(v).trim() === '') {
          field[prop] = t(msgKey)
        }
      }
      setIfEmpty('dayLabel', 'registration.dayLabelPlaceholder')
      setIfEmpty('dayPlaceholder', 'registration.dayPlaceholderPlaceholder')
      setIfEmpty('monthLabel', 'registration.monthLabelPlaceholder')
      setIfEmpty('monthPlaceholder', 'registration.monthPlaceholderPlaceholder')
      setIfEmpty('yearLabel', 'registration.yearLabelPlaceholder')
      setIfEmpty('yearPlaceholder', 'registration.yearPlaceholderPlaceholder')
    }
    const monthNamesSafe = computed(() => {
      const arr =
        props.field && Array.isArray(props.field.monthNames)
          ? props.field.monthNames
          : null
      if (arr && arr.length === 12) return arr
      // Fallback to 12 empty strings to avoid template errors during init
      return Array.from({ length: 12 }, () => '')
    })
    const setMonthName = (index, value) => {
      if (!props.field) return
      if (
        !Array.isArray(props.field.monthNames) ||
        props.field.monthNames.length !== 12
      ) {
        props.field.monthNames = [...defaultMonthNames.value]
      }
      // Ensure index is within bounds
      if (index >= 0 && index < 12) {
        props.field.monthNames.splice(index, 1, value)
      }
    }
    watch(
      () => props.field,
      (f) => ensureDateFieldDefaults(f),
      { deep: true, immediate: true }
    )
    watch(locale, () => {
      if (props.field?.type === 'date') ensureDateFieldDefaults(props.field)
    })
    const typeLabel = computed(() => {
      const m = {
        text: t('registration.fieldTypes.text'),
        email: t('registration.fieldTypes.email'),
        name: t('registration.fieldTypes.name'),
        number: t('registration.fieldTypes.number'),
        textarea: t('registration.fieldTypes.textarea'),
        select: t('registration.fieldTypes.select'),
        radio: t('registration.fieldTypes.radio'),
        checkbox: t('registration.fieldTypes.checkbox'),
        date: t('registration.fieldTypes.date'),
        time: t('registration.fieldTypes.time'),
        section: t('registration.fieldTypes.section'),
      }
      return props.field ? m[props.field.type] || props.field.type : ''
    })
    const updateValidation = (key, value) => {
      if (!props.field) return
      if (
        !props.field.validation ||
        typeof props.field.validation !== 'object'
      ) {
        props.field.validation = {}
      }
      const num = Number(value)
      props.field.validation[key] = isNaN(num) ? undefined : num
    }
    const hasAnyValidationControls = computed(() => {
      const type = props.field && props.field.type
      return type === 'number' || ['text', 'textarea'].includes(type)
    })

    return {
      t,
      typeLabel,
      defaultMonthNames,
      updateValidation,
      monthNamesSafe,
      setMonthName,
      hasAnyValidationControls,
    }
  },
}
</script>

<style scoped>
.field-editor-panel {
  background: var(--fb-surface, #ffffff);
  border: 1px solid var(--fb-border, #e4e4e7);
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  background: var(--fb-elevated, #faf9f6);
  border-bottom: 1px solid var(--fb-border, #e4e4e7);
}

.panel-header h4 {
  margin: 0;
  color: var(--fb-heading, #27272a);
  font-size: 1rem;
  font-weight: 600;
}
.field-type-badge {
  background: var(--fb-code-bg, #ebe8e3);
  color: var(--fb-muted, #52525b);
  border: 1px solid var(--fb-border, #e4e4e7);
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: none;
}

.editor-content {
  padding: 1rem;
  flex: 1 1 auto;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.month-names-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.month-name-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.month-name-caption {
  color: var(--fb-muted, #52525b);
  font-size: 0.72rem;
  line-height: 1.2;
}

.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: var(--fb-heading, #27272a);
  margin-bottom: 0.375rem;
  font-size: 0.9375rem;
}
.form-group.inline {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}
.form-group.inline label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0;
  cursor: pointer;
}
.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--fb-border-strong, #d4d4d8);
  border-radius: 6px;
  font-size: 0.875rem;
  box-sizing: border-box;
  background: var(--fb-surface, #fff);
  color: var(--fb-text, #18181b);
}
.form-group.inline input[type='checkbox'] {
  width: auto;
  padding: 0;
  margin: 0;
}
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--fb-focus, #52525b);
  box-shadow: 0 0 0 2px var(--fb-focus-ring, rgba(82, 82, 91, 0.22));
}

.form-group input.error {
  border-color: var(--fb-danger, #b91c1c);
  box-shadow: 0 0 0 2px rgba(185, 28, 28, 0.18);
}

.form-group input.auto-generated {
  background-color: var(--fb-elevated, #faf9f6);
  color: var(--fb-muted, #52525b);
  cursor: not-allowed;
}

.field-name-container {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.field-name-container input {
  flex: 1;
  min-width: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.option-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.option-item input {
  flex: 1;
}
.validation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.validation-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.validation-item label {
  font-size: 0.75rem;
  color: var(--fb-muted, #52525b);
  margin: 0;
}

.btn {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}
.btn-secondary {
  background-color: #52504d;
  color: #fafaf9;
}

.btn-secondary:hover {
  background-color: var(--fb-primary-hover, #27272a);
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--fb-border-strong, #d4d4d8);
  color: var(--fb-muted, #52525b);
}

.btn-outline:hover {
  background: var(--fb-hover-fill, #e4e4e7);
  color: var(--fb-heading, #27272a);
}

.btn-sm {
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
}

.options-list .btn-sm {
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  width: auto;
  align-self: flex-start;
}

.edit-name-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.field-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.option-actions {
  align-items: center;
}

.option-item .btn-icon {
  background: var(--fb-elevated, #fafafa);
  border: 1px solid var(--fb-border, #e4e4e7);
  cursor: pointer;
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 6px;
  color: var(--fb-muted, #52525b);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.option-item .btn-icon:hover:not(:disabled) {
  background-color: var(--fb-hover-fill, #e4e4e7);
  border-color: var(--fb-border-strong, #d4d4d8);
  color: var(--fb-heading, #27272a);
}

.option-item .btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-svg {
  width: 15px;
  height: 15px;
  fill: currentColor;
  display: block;
}

.option-item .btn-icon.btn-danger {
  color: var(--fb-danger, #b91c1c);
}

.option-item .btn-icon.btn-danger:hover:not(:disabled) {
  background: var(--fb-danger-soft, #fef2f2);
  color: var(--fb-danger-hover, #991b1b);
}

.btn-danger {
  color: var(--fb-danger, #b91c1c);
}

@media (max-width: 900px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .month-names-grid {
    grid-template-columns: 1fr;
  }
}
</style>
